/**
 * Centralized logger with production mode support
 * In production, console.* calls are no-ops to prevent sensitive data leakage
 * and maintain a professional appearance.
 * 
 * Debug mode can be enabled in production by adding ?debug=true to the URL
 */

type LogLevel = 'log' | 'info' | 'warn' | 'error' | 'debug'

type ScopedLogger = {
  log: (...args: any[]) => void
  info: (...args: any[]) => void
  warn: (...args: any[]) => void
  error: (...args: any[]) => void
  debug: (...args: any[]) => void
  group: (label: string) => void
  groupCollapsed: (label: string) => void
  groupEnd: () => void
  time: (label: string) => void
  timeEnd: (label: string) => void
  measure: <T>(label: string, fn: () => T | Promise<T>) => Promise<T> | T
}

class Logger {
  private isDebugMode(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    try {
      const params = new URLSearchParams(window.location.search);
      const debugParam = params.get('debug');
      if (debugParam === 'true') {
        return true;
      }
      if (debugParam === 'false') {
        return false;
      }
    } catch {
      // ignore parsing issues
    }
    return false;
  }

  private shouldLog(_level: LogLevel): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    return this.isDebugMode();
  }

  public isDebugSessionActive(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    return this.isDebugMode();
  }

  log(...args: any[]): void {
    if (this.shouldLog('log')) {
      console.log(...args)
    }
  }

  info(...args: any[]): void {
    if (this.shouldLog('info')) {
      console.info(...args)
    }
  }

  warn(...args: any[]): void {
    if (this.shouldLog('warn')) {
      console.warn(...args)
    }
  }

  error(...args: any[]): void {
    if (this.shouldLog('error')) {
      console.error(...args)
    }
  }

  debug(...args: any[]): void {
    if (this.shouldLog('debug')) {
      console.debug(...args)
    }
  }

  /**
   * Group console logs (noop in production unless debug mode)
   */
  group(label: string): void {
    if (this.isDebugSessionActive()) {
      console.group(label)
    }
  }

  groupEnd(): void {
    if (this.isDebugSessionActive()) {
      console.groupEnd()
    }
  }

  /**
   * Time measurement (noop in production unless debug mode)
   */
  time(label: string): void {
    if (this.isDebugSessionActive()) {
      console.time(label)
    }
  }

  timeEnd(label: string): void {
    if (this.isDebugSessionActive()) {
      console.timeEnd(label)
    }
  }

  groupCollapsed(label: string): void {
    if (this.isDebugSessionActive()) {
      console.groupCollapsed(label)
    }
  }

  async measure<T>(label: string, fn: () => T | Promise<T>): Promise<T> {
    const start = performance.now()
    try {
      const result = await fn()
      if (this.isDebugSessionActive()) {
        const duration = performance.now() - start
        console.info(`${label} completed in ${duration.toFixed(2)}ms`)
      }
      return result
    } catch (error) {
      if (this.isDebugSessionActive()) {
        console.error(`${label} failed after ${(performance.now() - start).toFixed(2)}ms`, error)
      }
      throw error
    }
  }

  scoped(scope: string): ScopedLogger {
    const prefix = `[${scope}]`
    return {
      log: (...args: any[]) => this.log(prefix, ...args),
      info: (...args: any[]) => this.info(prefix, ...args),
      warn: (...args: any[]) => this.warn(prefix, ...args),
      error: (...args: any[]) => this.error(prefix, ...args),
      debug: (...args: any[]) => this.debug(prefix, ...args),
      group: (label: string) => this.group(`${prefix} ${label}`),
      groupCollapsed: (label: string) => this.groupCollapsed(`${prefix} ${label}`),
      groupEnd: () => this.groupEnd(),
      time: (label: string) => this.time(`${prefix} ${label}`),
      timeEnd: (label: string) => this.timeEnd(`${prefix} ${label}`),
      measure: <T>(label: string, fn: () => T | Promise<T>) => this.measure(`${prefix} ${label}`, fn),
    }
  }

  /**
   * Table output (noop in production unless debug mode)
   */
  table(data: any): void {
    if (this.isDebugSessionActive()) {
      console.table(data)
    }
  }
}

export const logger = new Logger()

// For backwards compatibility, export individual methods
export const { log, info, warn, error, debug } = logger
export const isDebugSessionActive = () => logger.isDebugSessionActive()

export const createPlaybackLoggers = () => {
  const cache = new Map<string, ScopedLogger>()
  const get = (scope: string) => {
    if (!cache.has(scope)) {
      cache.set(scope, logger.scoped(scope))
    }
    return cache.get(scope)!
  }
  return {
    player: get('Player'),
    buffer: get('Buffer'),
    media: get('Media'),
    audio: get('Audio'),
    seek: get('Seek'),
    clip: get('Clip'),
  }
}
